import type { ChatToolCall } from "~/ai-chat/model/chat-message";

/** User-facing label for an in-flight tool call, e.g. `list_files` -> "Using list files…". */
export function toolActivityLabel(toolName: string): string {
  return `Using ${toolName.replace(/_/g, " ")}…`;
}

/**
 * The source to show for a call in the tool transcript: the code itself for `execute(code=...)`
 * (bare or group-prefixed, e.g. `istari_execute`), the arguments as JSON otherwise, or null when
 * the server dropped them.
 */
export function toolCallSource(call: ChatToolCall): { language: "python" | "json"; text: string } | null {
  if (call.args === null) return null;
  if (/(^|_)execute$/.test(call.tool) && typeof call.args.code === "string") {
    return { language: "python", text: call.args.code };
  }
  return { language: "json", text: JSON.stringify(call.args, null, 2) };
}

/** A call's duration for the tool transcript, e.g. `120 ms`, `1.2 s`. */
export function formatToolCallDuration(durationMs: number): string {
  return durationMs < 1000 ? `${durationMs} ms` : `${(durationMs / 1000).toFixed(1)} s`;
}

/** The one-line account of a turn's tool calls shown under its reply, e.g. `Ran 3 tool calls`. */
export function toolCallsLabel(calls: ChatToolCall[]): string {
  if (calls.length !== 1) return `Ran ${calls.length} tool calls`;
  const [call] = calls;
  const durationMs = call.outcome?.durationMs;
  return durationMs == null ? `Ran ${call.tool}` : `Ran ${call.tool} · ${formatToolCallDuration(durationMs)}`;
}
